/**
 * The supplement facts panel on a product page.
 *
 * The landing page's copy of this panel is part of the buy box and is driven by
 * PruvitBuy, which reads the buy box's own selection. A product page has no buy
 * box selection to read — it has the page's variant picker — so this is the
 * same panel with a different source of truth, not a second implementation of
 * the buy box.
 *
 * What it does: opens on whatever variant the page has selected, and is free to
 * move from there. Nothing it does touches the picker. Comparing two panels is
 * not choosing between them, and a reader who closes the modal should find the
 * page exactly as they left it — the same contract the buy box's copy states.
 */
(function () {
  var READY = 'pruvitSfpReady';

  function parse(root) {
    var node = root.querySelector('[data-pruvit-sfp-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function Panel(root) {
    this.root = root;
    this.variants = parse(root);
    this.flavorIdx = parseInt(root.dataset.flavorIndex, 10);
    this.formulaIdx = parseInt(root.dataset.formulaIndex, 10);
    this.dialog = root.querySelector('[data-pruvit-sfp-dialog]');
    this.select = root.querySelector('[data-pruvit-sfp-flavor]');
    this.tabs = root.querySelector('[data-pruvit-sfp-formula]');
    this.stage = root.querySelector('[data-pruvit-sfp-stage]');
    this.img = root.querySelector('[data-pruvit-sfp-img]');
    this.missing = root.querySelector('[data-pruvit-sfp-missing]');
    this.status = root.querySelector('[data-pruvit-sfp-status]');
    // Captured before anything rewrites it: the base every per-variant
    // description is built from.
    this.baseAlt = (this.img && this.img.getAttribute('alt')) || '';
    this.formula = null;

    var self = this;

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-pruvit-sfp-open]')) return self.open();
    });

    if (!this.dialog) return;

    this.dialog.addEventListener('click', function (event) {
      // A <dialog> reports a click on its own backdrop as a click on the
      // dialog element itself, which is what target === dialog catches.
      if (event.target === self.dialog || event.target.closest('[data-pruvit-sfp-close]')) {
        return self.dialog.close();
      }
      var tab = event.target.closest('[data-pruvit-sfp-formula] button');
      if (tab) {
        self.formula = tab.dataset.value;
        self.render();
      }
    });

    this.dialog.addEventListener('change', function (event) {
      if (event.target.closest('[data-pruvit-sfp-flavor]')) self.render();
    });
  }

  Panel.prototype.at = function (variant, index) {
    if (!variant || index < 0) return null;
    return variant.options[index] || null;
  };

  /**
   * What the page currently has selected, read when the panel opens rather
   * than tracked.
   *
   * There are two kinds of product page here and they hold the answer in
   * different places. K_1's has the Prüvit buy box, whose flavour and formula
   * live in its own option groups and never reach a form field — measured: no
   * [name="id"] and no cart form anywhere on that page. An ordinary Horizon
   * product page has the picker's hidden id input and no buy box. So the buy
   * box is asked first, and the form second.
   *
   * Reading rather than subscribing is deliberate. Both pickers announce
   * themselves with events, and listening to either would mean holding state
   * that can drift out of step with what is on screen; the DOM cannot. A panel
   * needs the answer once, at the moment it opens.
   */
  Panel.prototype.selection = function () {
    var scope =
      this.root.closest('[id^="shopify-section"], [id*="ProductInformation-"], [id*="QuickAdd-"]') || document;

    var picked = function (key) {
      var group = scope.querySelector('[data-pruvit-opts="' + key + '"]');
      if (!group) return null;
      var on = group.querySelector('[aria-pressed="true"], [aria-checked="true"]');
      return on ? on.dataset.value || null : null;
    };

    var flavor = picked('flavor');
    var formula = picked('formula');
    if (flavor || formula) return { flavor: flavor, formula: formula };

    var field = scope.querySelector('[name="id"]');
    var id = field && parseInt(field.value, 10);
    if (id) {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id !== id) continue;
        return {
          flavor: this.at(this.variants[i], this.flavorIdx),
          formula: this.at(this.variants[i], this.formulaIdx),
        };
      }
    }
    // The selected variant may have no panel of its own and so not be in the
    // payload at all. Opening on the controls' own defaults is the honest
    // answer then, rather than guessing at a neighbour.
    return { flavor: null, formula: null };
  };

  Panel.prototype.open = function () {
    if (!this.dialog) return;
    var current = this.selection();
    if (this.select && current.flavor) this.select.value = current.flavor;
    this.formula = current.formula;
    if (!this.formula && this.tabs) {
      var pressed = this.tabs.querySelector('[aria-pressed="true"]');
      this.formula = pressed ? pressed.dataset.value : null;
    }
    this.render();
    this.dialog.showModal();
  };

  Panel.prototype.render = function () {
    var flavor = this.select ? this.select.value : null;
    var formula = this.formula;

    if (this.tabs) {
      var tabs = this.tabs.querySelectorAll('button');
      for (var i = 0; i < tabs.length; i++) {
        tabs[i].setAttribute('aria-pressed', String(tabs[i].dataset.value === formula));
      }
    }

    // The panel belongs to the flavour and the formula. Size is not part of the
    // lookup — the same sachet is in every count — so the first variant that
    // carries a panel for this pair answers for all of them.
    var match = null;
    for (var j = 0; j < this.variants.length; j++) {
      var v = this.variants[j];
      if (flavor && this.at(v, this.flavorIdx) !== flavor) continue;
      if (formula && this.at(v, this.formulaIdx) !== formula) continue;
      match = v;
      break;
    }

    var describe = [flavor, formula].filter(Boolean).join(' ');
    if (this.img) this.img.alt = describe ? this.baseAlt + ' — ' + describe : this.baseAlt;

    if (!match) {
      // Say so plainly rather than leaving a shimmer up or asking the browser
      // for a src that is not there.
      if (this.img) this.img.removeAttribute('src');
      this.stage.classList.add('is-ready', 'is-missing');
      if (this.missing) this.missing.hidden = false;
      if (this.status) this.status.textContent = this.missing ? this.missing.textContent : '';
      return;
    }

    if (this.missing) this.missing.hidden = true;
    this.stage.classList.remove('is-missing');

    // A cached panel is already complete by the time src is assigned and fires
    // no load event at all, so the complete check is what stops the shimmer
    // sitting over a picture that has already arrived.
    if (this.img.getAttribute('src') === match.label && this.img.complete && this.img.naturalWidth) {
      this.stage.classList.add('is-ready');
      return;
    }

    var self = this;
    this.stage.classList.remove('is-ready');
    var settled = function () {
      self.stage.classList.add('is-ready');
      if (self.status) self.status.textContent = '';
    };
    this.img.onload = settled;
    this.img.onerror = settled;
    this.img.src = match.label;
    if (this.img.complete && this.img.naturalWidth) settled();
  };

  function init() {
    var roots = document.querySelectorAll('[data-pruvit-sfp]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset[READY]) continue;
      roots[i].dataset[READY] = 'true';
      new Panel(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
